﻿import {Paths} from "@2d-front-monorepo/core";
import {useEffect, useRef} from "react";
import {IMvvmContextState, MvvmContextState} from "./MvvmContextState";
import {MvvmValidation} from "./MvvmControlProps";

declare const process: { env?: { NODE_ENV?: string } } | undefined;
const IS_DEV = typeof process !== "undefined" && process?.env?.NODE_ENV !== "production";
const RE_REGISTER_WINDOW_MS = 1000;
const RE_REGISTER_THRESHOLD = 50;

interface ReRegisterCounter {
	count: number;
	windowStart: number;
	warned: boolean;
}

export function useRegisterValidation<T extends object, TValue>(context: IMvvmContextState<T>, property: Paths<T>, controlId: string, hidden: boolean | undefined, validation: MvvmValidation<TValue> | undefined): void {
	const counterRef = useRef<ReRegisterCounter | undefined>(undefined);

	// Register/update on every relevant change. registerValidation handles the
	// "already registered, update in place" case, so re-calling it cannot leak duplicates.
	// Deliberately NO cleanup here: unregister runs in the separate effect below so that
	// a churning `validation?.validate` reference does not wipe a freshly-set error via
	// MvvmContextState.unregisterValidation (which deletes the path's error entry).
	useEffect(function () {
		if (IS_DEV) {
			const now = performance.now();
			const counter = counterRef.current ??= {count: 0, windowStart: now, warned: false};
			if (now - counter.windowStart > RE_REGISTER_WINDOW_MS) {
				counter.count = 0;
				counter.windowStart = now;
				counter.warned = false;
			}
			counter.count++;
			if (!counter.warned && counter.count > RE_REGISTER_THRESHOLD) {
				counter.warned = true;
				// eslint-disable-next-line no-console
				console.warn(
					`[MvvmControls] validation re-registered ${counter.count}x in ${RE_REGISTER_WINDOW_MS}ms ` +
					`for "${property}" (control "${controlId}"). The validation object is likely being recreated ` +
					`on every render - wrap it in useMemo or define it outside the component.`
				);
			}
		}

		if (hidden) {
			(context as MvvmContextState<T>).unregisterValidation(property)
		} else {
			(context as MvvmContextState<T>).registerValidation(property, controlId, validation);
		}
	}, [context, property, controlId, hidden, validation?.required, validation?.validate]);

	// Unregister only when the control truly leaves the path (unmount, or context/property
	// change). This is where "errors die with their owning control" lives.
	useEffect(function () {
		return function () {
			(context as MvvmContextState<T>).unregisterValidation(property);
		}
	}, [context, property]);
}
